const keyPart=(value:unknown)=>String(value??'').trim().toUpperCase();

export const designKey=(value:any)=>[
  value.finished_length_mm,value.finished_width_mm,value.gusset_mm,
  value.production_width_mm,value.production_length_mm,
  keyPart(value.mouth_type||'Not specified'),keyPart(value.lamination||'NONE'),keyPart(value.hs_code),
].join('|');

export const materialKey=(value:any)=>[
  keyPart(value.quality),Number(value.gsm)||0,keyPart(value.origin||'Unspecified'),
].join('|');

export const designLabel=(design:any)=>design
  ?`${design.finished_length_mm} × ${design.finished_width_mm} × ${design.gusset_mm} · ${String(design.lamination||'NONE').toUpperCase()}`
  :'—';

export const materialLabel=(material:any)=>material
  ?`${material.quality} ${material.gsm} ${material.origin}`
  :'—';

export const recipeConstruction=(data:any,recipe:any)=>{
  if(!recipe)return '—';
  return (data.recipeLayers||[])
    .filter((layer:any)=>Number(layer.recipe_id)===Number(recipe.id))
    .sort((a:any,b:any)=>Number(a.layer_no)-Number(b.layer_no))
    .map((layer:any)=>materialLabel((data.paperMaterials||[]).find((material:any)=>Number(material.id)===Number(layer.material_id))))
    .join(' / ')||'—';
};

export const recipeLabel=(data:any,recipe:any)=>{
  if(!recipe)return '—';
  const design=(data.bagDesigns||[]).find((item:any)=>Number(item.id)===Number(recipe.bag_design_id));
  return `${designLabel(design)} · ${recipe.ply_count} ply · ${recipeConstruction(data,recipe)}`;
};

export const recipeSnapshot=(data:any,recipeId:any)=>{
  const recipe=(data.productionRecipes||[]).find((item:any)=>Number(item.id)===Number(recipeId));
  if(!recipe)return null;
  const design=(data.bagDesigns||[]).find((item:any)=>Number(item.id)===Number(recipe.bag_design_id));
  const layers=(data.recipeLayers||[])
    .filter((layer:any)=>Number(layer.recipe_id)===Number(recipe.id))
    .sort((a:any,b:any)=>Number(a.layer_no)-Number(b.layer_no))
    .map((layer:any)=>{
      const material=(data.paperMaterials||[]).find((item:any)=>Number(item.id)===Number(layer.material_id));
      return{layer_no:layer.layer_no,material_code:material?.material_code,quality:material?.quality,gsm:material?.gsm,origin:material?.origin};
    });
  return{
    recipe_code:recipe.recipe_code,recipe_name:recipe.recipe_name,version:recipe.version,
    bag_design_id:recipe.bag_design_id,design_code:design?.design_code,
    finished_length_mm:design?.finished_length_mm,finished_width_mm:design?.finished_width_mm,
    gusset_mm:design?.gusset_mm,production_width_mm:design?.production_width_mm,
    production_length_mm:design?.production_length_mm,mouth_type:design?.mouth_type,
    lamination:design?.lamination,ply_count:recipe.ply_count,paper_kg_per_bag:recipe.paper_kg_per_bag,layers,
  };
};

export function normalizeProductCatalog(input:any){
  const data={...input};
  const specs=[...(data.specs||[])],plies=[...(data.plies||[])];
  const hasRecipeCollection=Array.isArray(input?.productionRecipes);
  const hasCustomerProductRecipeCollection=Array.isArray(input?.customerProductRecipes);

  if(!(data.bagDesigns||[]).length){
    const byKey=new Map<string,any>();
    for(const spec of specs){
      const key=designKey(spec);
      if(!byKey.has(key))byKey.set(key,{...spec,id:byKey.size+1,design_code:`BD-${String(byKey.size+1).padStart(3,'0')}`,active:true});
    }
    data.bagDesigns=[...byKey.values()];
  }else data.bagDesigns=[...data.bagDesigns];

  if(!(data.paperMaterials||[]).length){
    const byKey=new Map<string,any>();
    for(const ply of plies){
      const key=materialKey(ply);
      if(!byKey.has(key))byKey.set(key,{id:byKey.size+1,material_code:`MAT-${String(byKey.size+1).padStart(3,'0')}`,quality:ply.quality,gsm:Number(ply.gsm),origin:ply.origin||'Unspecified',active:true});
    }
    data.paperMaterials=[...byKey.values()];
  }else data.paperMaterials=[...data.paperMaterials];

  const suppliedRecipes=[...(data.productionRecipes||[])];
  if(!(data.bomFamilies||[]).length){
    data.bomFamilies=data.bagDesigns.map((design:any,index:number)=>{
      const existing=suppliedRecipes
        .filter((recipe:any)=>Number(recipe.bag_design_id)===Number(design.id))
        .sort((a:any,b:any)=>Number(a.version)-Number(b.version)||Number(a.id)-Number(b.id))[0];
      return{id:index+1,bom_code:existing?.recipe_code||`BOM-${String(index+1).padStart(3,'0')}`,bag_design_id:design.id,active:true};
    });
  }else{
    const seenDesigns=new Set<number>();
    data.bomFamilies=[...data.bomFamilies]
      .sort((a:any,b:any)=>Number(a.id)-Number(b.id))
      .filter((family:any)=>{const designId=Number(family.bag_design_id);if(seenDesigns.has(designId))return false;seenDesigns.add(designId);return true});
    for(const design of data.bagDesigns){
      if(seenDesigns.has(Number(design.id)))continue;
      const id=Math.max(0,...data.bomFamilies.map((family:any)=>Number(family.id)||0))+1;
      data.bomFamilies.push({id,bom_code:`BOM-${String(id).padStart(3,'0')}`,bag_design_id:design.id,active:true});
      seenDesigns.add(Number(design.id));
    }
  }

  if(!hasRecipeCollection){
    const familyVersions=new Map<number,number>();
    data.productionRecipes=specs.map((spec:any,index:number)=>{
      const design=data.bagDesigns.find((item:any)=>designKey(item)===designKey(spec));
      const family=data.bomFamilies.find((item:any)=>Number(item.bag_design_id)===Number(design?.id));
      const version=(familyVersions.get(Number(family?.id))||0)+1;
      familyVersions.set(Number(family?.id),version);
      return{id:index+1,bom_family_id:family?.id,recipe_code:family?.bom_code,recipe_name:spec.name,bag_design_id:design?.id,legacy_master_spec_id:spec.id,version,status:'Approved',ply_count:Number(spec.ply_count),paper_kg_per_bag:spec.paper_kg??null,active:true};
    });
  }else{
    const usedVersions=new Map<number,Set<number>>();
    data.productionRecipes=[...data.productionRecipes]
      .sort((a:any,b:any)=>Number(a.id)-Number(b.id))
      .map((recipe:any)=>{
        const family=data.bomFamilies.find((item:any)=>Number(item.bag_design_id)===Number(recipe.bag_design_id));
        const familyId=Number(family?.id),used=usedVersions.get(familyId)||new Set<number>();
        let version=Math.max(1,Number(recipe.version)||1);
        while(used.has(version))version++;
        used.add(version);usedVersions.set(familyId,used);
        return{...recipe,bom_family_id:family?.id??recipe.bom_family_id,recipe_code:family?.bom_code??recipe.recipe_code,version};
      });
  }
  data.bomFamilies=data.bomFamilies.map((family:any)=>({...family,last_version:Math.max(Number(family.last_version)||0,...data.productionRecipes.filter((recipe:any)=>Number(recipe.bom_family_id)===Number(family.id)).map((recipe:any)=>Number(recipe.version)||0))}));

  if(!(data.recipeLayers||[]).length){
    let id=1;
    data.recipeLayers=data.productionRecipes.flatMap((recipe:any)=>plies
      .filter((ply:any)=>Number(ply.spec_id)===Number(recipe.legacy_master_spec_id))
      .sort((a:any,b:any)=>Number(a.sequence)-Number(b.sequence))
      .map((ply:any)=>({id:id++,recipe_id:recipe.id,layer_no:ply.sequence,material_id:data.paperMaterials.find((material:any)=>materialKey(material)===materialKey(ply))?.id})));
  }else data.recipeLayers=[...data.recipeLayers];

  data.products=(data.products||[]).map((product:any)=>{
    const recipe=data.productionRecipes.find((item:any)=>Number(item.id)===Number(product.default_recipe_id))
      ||data.productionRecipes.find((item:any)=>Number(item.legacy_master_spec_id)===Number(product.master_spec_id));
    const artworkKey=String(product.artwork_key??product.artwork_path??product.artwork_url??'').trim();
    const suppliedFiles=Array.isArray(product.artwork_files)?product.artwork_files:[];
    const artworkFiles=suppliedFiles.map((item:any)=>({reference:String(item?.reference??item?.artwork_key??item?.path??'').trim(),fileName:String(item?.fileName??item?.file_name??'Artwork attachment').trim()||'Artwork attachment',mimeType:String(item?.mimeType??item?.mime_type??'').trim()})).filter((item:any)=>item.reference).slice(0,3);
    if(!artworkFiles.length&&artworkKey)artworkFiles.push({reference:artworkKey,fileName:String(product.artwork_file_name||'Artwork attachment'),mimeType:''});
    return{...product,artwork_key:artworkFiles[0]?.reference||'',artwork_file_name:artworkFiles[0]?.fileName||'',artwork_files:artworkFiles,bag_design_id:product.bag_design_id??recipe?.bag_design_id??null,default_recipe_id:product.default_recipe_id??recipe?.id??null};
  });

  if(!hasCustomerProductRecipeCollection){
    data.customerProductRecipes=data.products.filter((product:any)=>product.default_recipe_id).map((product:any,index:number)=>({id:index+1,customer_product_id:product.id,recipe_id:product.default_recipe_id,is_default:true}));
  }else data.customerProductRecipes=[...data.customerProductRecipes];

  data.orderLines=(data.orderLines||[]).map((line:any)=>{
    if(line.production_recipe_id&&line.production_spec_snapshot)return line;
    const product=data.products.find((item:any)=>Number(item.id)===Number(line.customer_product_id));
    const recipe=data.productionRecipes.find((item:any)=>Number(item.id)===Number(product?.default_recipe_id));
    return{...line,production_recipe_id:line.production_recipe_id??recipe?.id??null,production_recipe_version:line.production_recipe_version??recipe?.version??null,production_spec_snapshot:line.production_spec_snapshot??recipeSnapshot(data,recipe?.id)};
  });

  return data;
}
